import { useContext } from 'react';
import PropTypes from 'prop-types';
import interpolateComponents from 'interpolate-components';
import classNames from 'clsx';
import { LocationAndLocalizationContext } from '../../../providers/LocationAndLocalizationProvider';
import getOverrideOrDefaultResourceKey from '../../../utils/getOverrideOrDefaultResourceKey';
import billingPeriods from '../../../utils/subscription/billingPeriod';
import { isDiscountEnabled } from '../../utils/subscriptionProductDetails';
import css from '../../payment/payment.module.scss';
import {
  SUBSCRIBE_MONTHLY_SETUPFEE_RADIO_TITLE_LABEL,
  SUBSCRIBE_MONTHLY_RADIO_TITLE_LABEL,
  SUBSCRIBE_YEARLY_RADIO_TITLE_LABEL,
  SUBSCRIBE_QUARTERLY_RADIO_TITLE_LABEL,
  SUBSCRIBE_WITH_DAYS_TRIAL_RADIO_SUBTITLE_LABEL,
  SUBSCRIBE_WITH_MONTHS_TRIAL_RADIO_SUBTITLE_LABEL,
  SUBSCRIBE_WITH_SPECIAL_TRIAL_RADIO_SUBTITLE_LABEL,
} from '../../../constants/localizations/subscription';

export default function RadioButtonLabel({ product }) {
  const { getLocalizedText, location } = useContext(
    LocationAndLocalizationContext,
  );

  const {
    billingPeriod,
    hasCouponBeenApplied,
    isMonthlyTrial,
    hasTrial,
    price,
    setupPrice,
    freeTrialLength,
  } = product;

  // when discount offers months, then use the month label for subtitle
  const subtitleLocalizationKey = isMonthlyTrial
    ? SUBSCRIBE_WITH_MONTHS_TRIAL_RADIO_SUBTITLE_LABEL
    : SUBSCRIBE_WITH_DAYS_TRIAL_RADIO_SUBTITLE_LABEL;

  let titleKey = SUBSCRIBE_YEARLY_RADIO_TITLE_LABEL;
  let testIdType = 'Yearly';

  if (billingPeriod === billingPeriods.monthly) {
    titleKey = SUBSCRIBE_MONTHLY_RADIO_TITLE_LABEL;
    testIdType = 'Monthly';
  } else if (billingPeriod === billingPeriods.quarterly) {
    titleKey = SUBSCRIBE_QUARTERLY_RADIO_TITLE_LABEL;
    testIdType = 'Quarterly';
  }

  titleKey = setupPrice
    ? SUBSCRIBE_MONTHLY_SETUPFEE_RADIO_TITLE_LABEL
    : titleKey;

  const title = getLocalizedText(
    getOverrideOrDefaultResourceKey(getLocalizedText, location, titleKey),
  );
  const subtitle = setupPrice
    ? getLocalizedText(SUBSCRIBE_WITH_SPECIAL_TRIAL_RADIO_SUBTITLE_LABEL)
    : getLocalizedText(
        getOverrideOrDefaultResourceKey(
          getLocalizedText,
          location,
          subtitleLocalizationKey,
        ),
      );

  const shouldShowDiscountedDetails =
    hasCouponBeenApplied || isDiscountEnabled(location) || !!setupPrice;
  const priceCss = shouldShowDiscountedDetails
    ? css.productTitleDiscounted
    : css.productTitle;
  const subtitleCss = hasTrial
    ? css.productSubtitle
    : css.hiddenProductSubtitle;

  const displayPrice = setupPrice || price;

  return (
    <div className={css.radioButtonText}>
      <span data-testid={`title${testIdType}`} className={css.productTitle}>
        {interpolateComponents({
          mixedString: title,
          components: {
            price: (
              <span
                data-testid={`price${testIdType}`}
                className={classNames(priceCss, {
                  [css.noPadding]: setupPrice,
                })}
              >
                {displayPrice}
              </span>
            ),
            freeTrialLength: (
              <span data-testid={`trialPeriod${testIdType}`}>
                {freeTrialLength}
              </span>
            ),
          },
        })}
      </span>
      <br className={css.productDescriptionBreak} />
      <span data-testid={`subtitle${testIdType}`} className={subtitleCss}>
        {interpolateComponents({
          mixedString: subtitle,
          components: {
            freeTrialLength: (
              <span data-testid={`trialPeriod${testIdType}`}>
                {freeTrialLength}
              </span>
            ),
          },
        })}
      </span>
    </div>
  );
}

RadioButtonLabel.propTypes = {
  product: PropTypes.object.isRequired,
};
